import { FormCloseIcon, FormEditIcon, MapIcon, PlusIconBlue } from 'assets/icons';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import AddressModal from 'containers/jobs/components/AddressModal';
import { useAppSelector } from 'hooks';
import { FC, useEffect, useState } from 'react';
import { AddCustomerAddressPayload, CustomerObject } from 'containers/customers/customers.model';
import { useAddCustomerAddressMutation, useEditCustomerAddressMutation } from 'containers/customers/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { AddressField, Customer } from 'containers/invoices/invoices.model';
import { getAddressAsString } from 'containers/jobs/utils';

interface InvoiceServiceAddressSectionProps {
  className?: string;
  currentCustomer: CustomerObject | Customer;
  currentServiceAddress: AddressField;
  onUpdateServiceAddress: (address: AddressField) => void;
  isEditCustomerAllowed: boolean;
}

const InvoiceServiceAddressSection: FC<InvoiceServiceAddressSectionProps> = ({
  currentCustomer,
  currentServiceAddress,
  onUpdateServiceAddress,
  isEditCustomerAllowed
}) => {
  const [openServiceAddressModal, setOpenServiceAddressModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [
    addCustomerAddressMutation,
    { isSuccess: customerAddressSuccess, data: customerAddress, isLoading: loadingCustomerAddressAPI }
  ] = useAddCustomerAddressMutation();
  const [
    editCustomerAddressMutation,
    { isSuccess: isEditAddressSuccess, data: editAddressData, isLoading: isLoadingEditAddress }
  ] = useEditCustomerAddressMutation();

  const currentTeam = useAppSelector(selectCurrentTeam);

  useEffect(() => {
    if (customerAddressSuccess) onUpdateServiceAddress(customerAddress);
  }, [customerAddressSuccess, loadingCustomerAddressAPI]);

  useEffect(() => {
    if (isEditAddressSuccess) onUpdateServiceAddress(editAddressData);
  }, [isEditAddressSuccess]);

  const handleOpenServiceFeeModal = () => {
    if (isEditCustomerAllowed) setOpenServiceAddressModal(true);
  };

  const closeServiceAddressModal = () => {
    setOpenServiceAddressModal(false);
    setIsEdit(false);
  };

  const onAddressAddition = addressDetails => {
    const addressPayload: AddCustomerAddressPayload = {
      ...addressDetails,
      teamId: currentTeam.id.toString(),
      customerId: currentCustomer['ID'],
      isBillingSameAsService: false,
      addressId: 0
    };
    if (isEdit) {
      editCustomerAddressMutation({ id: currentServiceAddress.id.toString(), body: addressPayload });
    } else {
      addCustomerAddressMutation(addressPayload);
    }
    closeServiceAddressModal();
  };

  const handleRemoveServiceAddressFromInvoice = () => {
    onUpdateServiceAddress(null);
  };

  const handleEditServiceAddress = () => {
    setIsEdit(true);
    setOpenServiceAddressModal(true);
  };

  return (
    <div className="relative">
      <div className="pb-2.5 text-17px font-semibold text-heading">Service address</div>
      <div className="relative rounded border border-borderGray p-5">
        {currentServiceAddress ? (
          <div className="flex items-start gap-1">
            <MapIcon />
            <div>
              <div className="text-sbase font-semibold text-primaryText">
                {getAddressAsString(currentServiceAddress) ?? '-'}
              </div>
              {currentServiceAddress.addressNote && (
                <div className="text-sbase font-semibold text-primaryText">{currentServiceAddress.addressNote}</div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`flex cursor-pointer items-center gap-1
                      ${!isEditCustomerAllowed && 'pointer-events-none opacity-50'}`}
            onClick={handleOpenServiceFeeModal}
            role="presentation">
            <PlusIconBlue />
            <div className="text-17px font-semibold text-secondary">Add a service address</div>
          </div>
        )}
        {currentServiceAddress && isEditCustomerAllowed && (
          <div className="absolute top-1/2 -right-6 -translate-y-1/2 space-y-1.5">
            <FormCloseIcon className="cursor-pointer" onClick={handleRemoveServiceAddressFromInvoice} />
            <FormEditIcon className="cursor-pointer" onClick={handleEditServiceAddress} />
          </div>
        )}
      </div>
      {openServiceAddressModal && (
        <AddressModal
          onClose={closeServiceAddressModal}
          type={ADDRESS_TYPE.SERVICE}
          onAddAddress={onAddressAddition}
          isEdit={isEdit}
          initialAddress={currentServiceAddress}
          isLoading={loadingCustomerAddressAPI || isLoadingEditAddress}
        />
      )}
    </div>
  );
};

export default InvoiceServiceAddressSection;
